import styled, {css} from 'styled-components'
import {DatePicker, Select as SelectAnt} from 'antd';
import { getSize, SizeTypes } from '../utils/getSize';
import {
    borderColor,
    borderRadius,
    fontFamily,
    inputBackground,
    inputColor,
    inputPadding,
    primaryColor,
    inputSize,
    inputBackgroundFocus,
    inputBackgroundDarkMode,
    inputColorDarkMode, media,
    mutedForeground,
} from '../utils/variables';

interface InputProps {
    login?: boolean;
    extent?: SizeTypes;
    darkMode?: boolean;
    minMaxValueCheck?: boolean;
}

export const inputStyles = ({ login ,extent, darkMode }: InputProps) => `
    border: unset;
    flex: 1;
    border: 1px solid ${darkMode ? 'transparent' : borderColor};
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    border-radius: ${borderRadius};
    background-color: ${darkMode ? inputBackgroundDarkMode : 'var(--card)'};
    padding: ${inputPadding};
    color: ${darkMode ? inputColorDarkMode : inputColor};
    font-family: ${fontFamily};
    width: ${extent
        ? `${getSize(extent, inputSize)}`
        : 'unset'
    };

    ::placeholder {
        color: ${mutedForeground};
    }
    :hover {
        border-color: ${primaryColor};
    }
    :focus {
        border: 1px solid ${primaryColor};
        box-shadow: 0 0 0 3px rgba(43, 127, 255, 0.12);
        background-color: ${darkMode ? inputBackgroundFocus : 'var(--card)'};
        outline: none;
    }
    
    ::-webkit-outer-spin-button,
        ::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }
        -moz-appearance: textfield;
    
    ${media.mobile && (login === false || login === undefined) ? `
        width: 125px;
    ` : ''}
`;

export const Form = styled.form``;

export const Date = styled(DatePicker)<InputProps>`
    ${({ extent, darkMode }) => inputStyles({ extent, darkMode })}
`

export const TextInput = styled.input<InputProps>`
    ${({minMaxValueCheck}) => minMaxValueCheck && css`
        :invalid {
            border: 1px solid red;
        }
        :hover:invalid, :focus:invalid {
            border: 2px solid red;
        }
    `}
    
    ${({ login, extent, darkMode }) => inputStyles({ login, extent, darkMode })}
`;

export const NumberInput = styled.input<InputProps>`
    ::-webkit-outer-spin-button,
    ::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
    -moz-appearance: textfield;
    :invalid {
        border: 1px solid red;
    }
    :hover:invalid, :focus:invalid {
        border: 2px solid red;
    }
    ${({ extent, darkMode }) => inputStyles({ extent, darkMode })}
`;

export const TextArea = styled.textarea<InputProps>`
    font-family: ${fontFamily};
    ${({ extent, darkMode }) => inputStyles({ extent, darkMode })}
`;

export const Select = styled.select<InputProps>`
    ${({ extent, darkMode }) => inputStyles({ extent, darkMode })}
`;

export const SelectView = styled.select<InputProps>`
    ${({ extent, darkMode }) => inputStyles({ extent, darkMode })}
`;

export const AntSelect = styled(SelectAnt)<InputProps>`
    .ant-select-selection-placeholder {
        color: ${mutedForeground};
    }
    
    .ant-select-arrow {
        display: none;
    }

    .ant-select-selector {
        overflow: hidden;
    }
    
    ${({ extent, darkMode }) => inputStyles({ extent, darkMode })}
`;

export const ColorInput = styled.input.attrs({type: 'color'})`
    width: 40px;
    height: 40px;
    padding: 4px;
    border: 1px solid ${borderColor};
    border-radius: ${borderRadius};
    background: var(--input);
    cursor: pointer;
    color-scheme: light;
    flex-shrink: 0;

    &::-webkit-color-swatch-wrapper {
        padding: 0;
    }

    &::-webkit-color-swatch {
        border: 1px solid ${borderColor};
        border-radius: 4px;
    }

    &::-moz-color-swatch {
        border: 1px solid ${borderColor};
        border-radius: 4px;
    }

    &:focus-visible {
        outline: 2px solid ${primaryColor};
        outline-offset: 2px;
    }

    @media (max-width: 1200px) {
        width: 34px;
        height: 34px;
    }
`;

export const Required = styled.span`
    font-size: 14px;
    font-weight: 800;
    position: relative;
    top: -2px;
    color: red;
`;

export const StyledWrapper = styled.div`
    position: relative;
    width: 100%;
`;

export const StyledPreview = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    min-height: 150px;
    padding: 8px;
    white-space: pre-wrap;
    word-break: break-word;
    pointer-events: none;
    color: var(--foreground);
    line-height: 1.5;
    z-index: 1;
    font-family: ${fontFamily};
    font-size: 14px;
    letter-spacing: 0;
    box-sizing: border-box;
`;

export const StyledHighlight = styled.span`
    display: inline;
    background-color: var(--search-highlight);
    padding: 0 2px;
    border-radius: ${borderRadius};
    line-height: inherit;
    font-size: inherit;
    white-space: pre-wrap;
    word-break: break-word;

    .highlighted {
        background-color: var(--surface-subtle);
        border-radius: 4px;
        padding: 0 2px;
        margin: 0;
        display: inline;
        white-space: pre-wrap;
    }
`;

export const TransparentTextArea = styled(TextArea)`
  position: relative;
  z-index: 2;   
  background: transparent;
  color: var(--foreground);
  width: 100%;
  min-height: 150px;
  padding: 8px;
  resize: vertical;
`;

